import quagga, { QuaggaJSResultObject } from "@ericblade/quagga2";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import {
  RE_SCAN_DELAY,
  ScannerState,
  debugConfig,
  locatorConfig,
} from "./constants";
import { BarcodeSettingsContext, StreamContext } from "./context";
import { ScannerOptions, StreamContextType, UseScannerReturn } from "./types";
import { readerDataToQuaggaReaders, toDecodeArea } from "./utils";
import { ScannerStateMachine } from "./ScannerStateMachine";
import { useScheduler } from "../scheduler";

export const useBarcodeSettingsProvider = () => {
  const c = useContext(BarcodeSettingsContext);
  if (!c)
    throw Error(
      "useBarcodeSettingsProvider must be used within BarcodeSettingsProvider"
    );
  return c;
};

export const useStreamProvider = () => {
  const c = useContext(StreamContext);
  if (!c) throw Error("useStreamProvider must be used within StreamProvider");
  return c;
};

type UseScannerParams = {
  videoRef: StreamContextType["videoRef"];
  streamRef: StreamContextType["streamRef"];
  cleanupStream: StreamContextType["cleanupStream"];
  setVideoSource: StreamContextType["setVideoSource"];
  setIsCameraReady: (readyState: boolean) => void;
};

export function useScanner({
  videoRef,
  streamRef,
  cleanupStream,
  setVideoSource,
  setIsCameraReady,
}: UseScannerParams): UseScannerReturn {
  const { readers } = useBarcodeSettingsProvider();

  const { schedule, cancel: cancelScheduledEvent } =
    useScheduler(RE_SCAN_DELAY);

  const detectedCallbackRef =
    useRef<(result: QuaggaJSResultObject) => void | null>(null);

  const [state, setState] = useState<ScannerState>(ScannerState.STOPPED);

  const stateRef = useRef(state);

  stateRef.current = state;

  const targetRef = useRef<HTMLDivElement>(null);

  const startScanning = ({
    scannableArea,
    onBarCodeScanned,
  }: ScannerOptions) => {
    let area = undefined;
    if (scannableArea && videoRef.current) {
      area = toDecodeArea(
        scannableArea,
        videoRef.current.clientWidth,
        videoRef.current.clientHeight
      );
    }

    destroy();

    quagga.init(
      {
        inputStream: {
          constraints: {},
          name: "Live",
          type: "LiveStream",
          target: targetRef.current as any,
          willReadFrequently: true,
        },
        decoder: {
          readers: readerDataToQuaggaReaders(readers),
          debug: debugConfig,
        },
        locate: true,
        locator: locatorConfig,
      },
      function (err) {
        if (err) {
          console.log(err);
          return;
        }
        setState(ScannerState.RUNNING);
        quagga.start();
      }
    );
    detectedCallbackRef.current = errorCheck(onBarCodeScanned);
    quagga.onDetected(detectedCallbackRef.current);
    quagga.onProcessed(handleProcessed);
  };

  const streamCamVideo = () => {
    const constraints = {
      video: {
        facingMode: "environment",
      },
    };

    navigator.mediaDevices
      .getUserMedia(constraints)
      .then(function (mediaStream) {
        cleanupStream();
        streamRef.current = mediaStream;
        const video = videoRef.current;
        if (!video) return;

        setVideoSource(video, streamRef.current);

        video.oncanplay = function (e) {
          video.play();
        };
        video.onplaying = function (e) {
          setIsCameraReady(true);
        };
      })
      .catch(console.log);
  };

  const errorCheck = useCallback(
    (onBarCodeScanned: ScannerOptions["onBarCodeScanned"]) => (result: any) => {
      const err = getMedianOfCodeErrors(result.codeResult.decodedCodes);
      // if Quagga is at least 75% certain that it read correctly, then accept the code.
      if (err < 0.25) {
        pause();
        onBarCodeScanned(result);
      }
    },
    [pause]
  );

  const handleProcessed = (result: any) => {
    const drawingCtx = quagga.canvas.ctx.overlay;
    drawingCtx.font = "24px Arial";
    drawingCtx.fillStyle = "green";

    if (result) {
      if (result.boxes) {
        clearScanLines();
        result.boxes
          .filter((box: any) => box !== result.box)
          .forEach((box: any) => {
            quagga.ImageDebug.drawPath(box, { x: 0, y: 1 }, drawingCtx, {
              color: "purple",
              lineWidth: 2,
            });
          });
      }
      if (result.box) {
        quagga.ImageDebug.drawPath(result.box, { x: 0, y: 1 }, drawingCtx, {
          color: "blue",
          lineWidth: 2,
        });
      }
      if (result.codeResult && result.codeResult.code) {
        quagga.ImageDebug.drawPath(
          result.line,
          { x: "x", y: "y" },
          drawingCtx,
          { color: "green", lineWidth: 3 }
        );
      }
    }
  };

  function clearScanLines() {
    const drawingCtx = quagga.canvas.ctx.overlay;
    const drawingCanvas = quagga.canvas.dom.overlay;

    if (!drawingCtx) return;

    drawingCtx.clearRect(
      0,
      0,
      parseInt(drawingCanvas.getAttribute("width")!),
      parseInt(drawingCanvas.getAttribute("height")!)
    );
  }

  function destroy() {
    detectedCallbackRef.current &&
      quagga.offDetected(detectedCallbackRef.current);
    quagga.offProcessed(handleProcessed);
    cancelScheduledEvent();
    setState(ScannerState.STOPPED);
    quagga.stop();
  }

  function pause() {
    cancelScheduledEvent();
    const fsm = new ScannerStateMachine();
    if (!fsm.canTransition(stateRef.current, ScannerState.PAUSED)) {
      return;
    }
    quagga.pause();
    videoRef.current?.pause();
    setState(ScannerState.PAUSED);
  }

  function resume() {
    videoRef.current?.play().then(() => {
      // await next render cycle so we dont rescan the same barcode
      clearScanLines();

      schedule(() => {
        setState(ScannerState.RUNNING);
        quagga.start();
      });
    });
  }

  useEffect(() => {
    quagga.setReaders(readerDataToQuaggaReaders(readers));
  }, [readers]);

  return {
    targetRef,
    videoRef,
    state,
    startScanning,
    streamCamVideo,
    pause,
    resume,
    destroy,
  };
}

function getMedian(arr: any[]) {
  const newArr = [...arr]; // copy the array before sorting, otherwise it mutates the array passed in, which is generally undesireable
  newArr.sort((a, b) => a - b);
  const half = Math.floor(newArr.length / 2);
  if (newArr.length % 2 === 1) {
    return newArr[half];
  }
  return (newArr[half - 1] + newArr[half]) / 2;
}

function getMedianOfCodeErrors(decodedCodes: any[]) {
  const errors = decodedCodes.flatMap((x) => x.error);
  const medianOfErrors = getMedian(errors);
  return medianOfErrors;
}
